import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ArrowsClockwise, Plus, ShoppingCart, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import CampaignsTab from "./shopnomix/CampaignsTab";
import AssignmentsTab from "./shopnomix/AssignmentsTab";
import ReportsTab from "./shopnomix/ReportsTab";
import LocalesTab from "./shopnomix/LocalesTab";
import ReconciliationTab from "./shopnomix/ReconciliationTab";
import RunsTab from "./shopnomix/RunsTab";
import CreateCampaignDialog from "./shopnomix/CreateCampaignDialog";
import ShareReportDialog from "./shopnomix/ShareReportDialog";
import AssignDialog from "./shopnomix/AssignDialog";
import EditAssignDialog from "./shopnomix/EditAssignDialog";
import ViewLocalesDialog from "./shopnomix/ViewLocalesDialog";

/**
 * Shopnomix admin — manages Amazon EPC campaigns + per-publisher
 * assignments. Each assignment has a `shave_cents` override (default
 * 1¢ in the $0.10–$0.27 zone). Pub earnings come from Shopnomix's
 * daily revenue ÷ clicks → shave engine → pub_epc × clicks. Pubs see
 * only their pub_epc, never feed_epc.
 *
 * This page is the orchestrator. Tab bodies + dialogs live in
 * `./shopnomix/*` so each concern is isolated and re-orderable.
 */
export default function AdminShopnomix() {
  const [campaigns, setCampaigns] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [campaignReport, setCampaignReport] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [openReconcDate, setOpenReconcDate] = useState(null);
  // Date window for Reconciliation tab. Defaults to "" → server picks
  // smart default (latest synced date − 30 days). User can override via
  // the date pickers in the Reconciliation tab header.
  const [reconcStart, setReconcStart] = useState("");
  const [reconcEnd, setReconcEnd] = useState("");
  // Tracks the window the server actually used (echoed in the response)
  // so the date inputs can show the smart default on first paint.
  const [activeReconcWindow, setActiveReconcWindow] = useState({ start: "", end: "" });
  const [runs, setRuns] = useState([]);
  const [locales, setLocales] = useState(null);
  const [loading, setLoading] = useState(true);

  // ─── Dialog open state ─────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ label: "", campaign_id: "", reporting_token: "", redirect_host: "" });
  const [creating, setCreating] = useState(false);

  const [shareOpen, setShareOpen] = useState(false);
  const [shareRecipients, setShareRecipients] = useState("kevin@theredclub.com, ashok@theredclub.com");
  const [shareLabel, setShareLabel] = useState("RedClub weekly report");
  const [sharing, setSharing] = useState(false);
  const [shareResult, setShareResult] = useState(null);

  const [assignOpen, setAssignOpen] = useState(null);
  const [assignPubId, setAssignPubId] = useState("");
  const [assignShave, setAssignShave] = useState("0.01");
  const [assignPassthrough, setAssignPassthrough] = useState(false);
  const [assignLocales, setAssignLocales] = useState(["amazon.com"]);
  const [assigning, setAssigning] = useState(false);

  const [editAssignOpen, setEditAssignOpen] = useState(null);
  const [editShave, setEditShave] = useState("0.01");
  const [editPassthrough, setEditPassthrough] = useState(false);
  const [editLocales, setEditLocales] = useState(["amazon.com"]);
  const [savingShave, setSavingShave] = useState(false);

  const [viewLocalesFor, setViewLocalesFor] = useState(null);
  const [syncingId, setSyncingId] = useState(null);

  const [localeCatalog, setLocaleCatalog] = useState([]);

  // ─── Data fetching ─────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      // Reconciliation date params — only attach when the user has
      // overridden the smart default; otherwise let the server pick.
      const reconcParams = {};
      if (reconcStart) reconcParams.start_date = reconcStart;
      if (reconcEnd) reconcParams.end_date = reconcEnd;
      const [c, a, p, s, cr, dr, r, dom, loc] = await Promise.all([
        API.get("/admin/shopnomix/campaigns"),
        API.get("/admin/shopnomix/assignments"),
        API.get("/admin/publishers"),
        API.get("/admin/shopnomix/reports/summary"),
        API.get("/admin/shopnomix/reports/campaigns", { params: reconcParams }),
        API.get("/admin/shopnomix/reports/daily", { params: reconcParams }),
        API.get("/admin/shopnomix/sync/runs?limit=15"),
        API.get("/admin/shopnomix/reports/domains"),
        API.get("/admin/shopnomix/locales"),
      ]);
      setCampaigns(c.data?.campaigns || []);
      setAssignments(a.data?.assignments || []);
      setPublishers(p.data || []);
      setSummary(s.data || null);
      setCampaignReport(cr.data || null);
      setDailyReport(dr.data || null);
      setActiveReconcWindow({
        start: cr.data?.start_date || "",
        end: cr.data?.end_date || "",
      });
      setRuns(r.data?.runs || []);
      setLocales(dom.data || null);
      setLocaleCatalog(loc.data?.locales || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Shopnomix data");
    } finally {
      setLoading(false);
    }
  }, [reconcStart, reconcEnd]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ─── Campaign CRUD ─────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.label || !form.campaign_id || !form.reporting_token) {
      toast.error("Label, campaign_id, and reporting_token are required");
      return;
    }
    setCreating(true);
    try {
      await API.post("/admin/shopnomix/campaigns", form);
      toast.success(`Created campaign: ${form.label}`);
      setCreateOpen(false);
      setForm({ label: "", campaign_id: "", reporting_token: "", redirect_host: "" });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const handleTest = async (c) => {
    const t = toast.loading(`Probing Shopnomix for "${c.label}"...`);
    try {
      const { data } = await API.post(`/admin/shopnomix/campaigns/${c.id}/test`);
      toast.dismiss(t);
      toast.success(`✓ ${c.label}: ${data.message}`);
    } catch (err) {
      toast.dismiss(t);
      toast.error(err.response?.data?.detail || "Test failed");
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.label}"?`)) return;
    try {
      await API.delete(`/admin/shopnomix/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  // ─── Assignments ───────────────────────────────────────────────
  const handleAssign = async () => {
    if (!assignPubId || !assignOpen) return;
    setAssigning(true);
    try {
      await API.post("/admin/shopnomix/assignments", {
        publisher_id: assignPubId,
        campaign_doc_id: assignOpen.id,
        shave_cents: parseFloat(assignShave),
        passthrough_above_cap: assignPassthrough,
        enabled_locales: assignLocales,
      });
      toast.success(`Assigned ${assignOpen.label} to publisher · ${assignLocales.length} locale(s)`);
      setAssignOpen(null);
      setAssignPubId("");
      setAssignShave("0.01");
      setAssignPassthrough(false);
      setAssignLocales(["amazon.com"]);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Assign failed");
    } finally {
      setAssigning(false);
    }
  };

  const openEditShave = (a) => {
    setEditAssignOpen(a);
    setEditShave(String(a.shave_cents ?? 0.01));
    setEditPassthrough(Boolean(a.passthrough_above_cap));
    setEditLocales(
      Array.isArray(a.enabled_locales) && a.enabled_locales.length
        ? a.enabled_locales
        : ["amazon.com"]
    );
  };

  const handleSaveShave = async () => {
    if (!editAssignOpen) return;
    setSavingShave(true);
    try {
      const { data } = await API.patch(
        `/admin/shopnomix/assignments/${editAssignOpen.id}`,
        {
          shave_cents: parseFloat(editShave),
          passthrough_above_cap: editPassthrough,
          enabled_locales: editLocales,
        },
      );
      toast.success(
        `Payout config updated` +
        (data?.re_shaved_rows ? ` · re-shaved ${data.re_shaved_rows} day(s)` : "")
      );
      setEditAssignOpen(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingShave(false);
    }
  };

  // Locale toggles — passed to the dialogs as plain callbacks.
  const toggleAssignLocale = (dom) => {
    setAssignLocales((prev) =>
      prev.includes(dom) ? prev.filter((d) => d !== dom) : [...prev, dom]
    );
  };
  const toggleEditLocale = (dom) => {
    setEditLocales((prev) =>
      prev.includes(dom) ? prev.filter((d) => d !== dom) : [...prev, dom]
    );
  };

  const handleRevoke = async (a) => {
    if (!window.confirm(`Revoke ${a.campaign_label} from ${a.publisher_email}?`)) return;
    try {
      await API.delete(`/admin/shopnomix/assignments/${a.id}`);
      toast.success("Assignment revoked");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Revoke failed");
    }
  };

  const handleSync = async (campaignDocId = null) => {
    setSyncingId(campaignDocId || "all");
    try {
      const url = campaignDocId
        ? `/admin/shopnomix/aggregates/sync?campaign_doc_id=${campaignDocId}&days=30`
        : `/admin/shopnomix/aggregates/sync?days=30`;
      const { data } = await API.post(url);
      const totals = (data.results || []).reduce(
        (acc, r) => ({ total: acc.total + (r.total || 0), upserted: acc.upserted + (r.upserted || 0) }),
        { total: 0, upserted: 0 }
      );
      toast.success(`Sync done: ${totals.total} rows · ${totals.upserted} upserted`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <div className="p-8" data-testid="admin-shopnomix">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-slate-400">Shopnomix</p>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <ShoppingCart size={26} weight="bold" /> Amazon EPC feeds
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Daily aggregates from <span className="font-mono text-xs">/v1/reporting/source</span> → shave engine → pub_epc.
            We pay <span className="font-bold">100% pass-through</span> below $0.10, shave 1¢ in the mid range, cap at $0.27.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShareOpen(true)} data-testid="sn-share-report-btn">
            <PaperPlaneTilt size={16} className="mr-1.5" />
            Email report
          </Button>
          <Button variant="outline" onClick={() => handleSync()} disabled={syncingId === "all"} data-testid="sn-sync-all-btn">
            <ArrowsClockwise size={16} className="mr-1.5" />
            {syncingId === "all" ? "Syncing…" : "Sync all"}
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-[#002FA7] hover:bg-[#002089] text-white" data-testid="sn-new-campaign-btn">
            <Plus size={16} className="mr-1.5" /> New campaign
          </Button>
        </div>
      </div>

      <Tabs defaultValue="campaigns" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="campaigns" data-testid="tab-campaigns">Campaigns ({campaigns.length})</TabsTrigger>
          <TabsTrigger value="assignments" data-testid="tab-assignments">
            Assignments ({assignments.filter((a) => a.status === "active").length})
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Per-pub Reports</TabsTrigger>
          <TabsTrigger value="locales" data-testid="tab-locales">By locale</TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="tab-reconciliation">Per-campaign Reconciliation</TabsTrigger>
          <TabsTrigger value="runs" data-testid="tab-runs">Sync runs ({runs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns">
          <CampaignsTab
            campaigns={campaigns}
            loading={loading}
            syncingId={syncingId}
            onTest={handleTest}
            onSync={handleSync}
            onOpenAssign={(c) => {
              setAssignOpen(c);
              setAssignPubId("");
              setAssignShave("0.01");
              setAssignPassthrough(false);
              setAssignLocales(["amazon.com"]);
            }}
            onDelete={handleDelete}
          />
        </TabsContent>

        <TabsContent value="assignments">
          <AssignmentsTab
            assignments={assignments}
            loading={loading}
            onViewLocales={setViewLocalesFor}
            onEditAssignment={openEditShave}
            onRevoke={handleRevoke}
          />
        </TabsContent>

        <TabsContent value="reports">
          <ReportsTab summary={summary} />
        </TabsContent>

        <TabsContent value="locales">
          <LocalesTab locales={locales} />
        </TabsContent>

        <TabsContent value="reconciliation">
          <ReconciliationTab
            campaignReport={campaignReport}
            dailyReport={dailyReport}
            openReconcDate={openReconcDate}
            onToggleDate={setOpenReconcDate}
            reconcStart={reconcStart}
            reconcEnd={reconcEnd}
            onWindowChange={(s, e) => {
              setReconcStart(s);
              setReconcEnd(e);
            }}
            activeWindow={activeReconcWindow}
            loading={loading}
          />
        </TabsContent>

        <TabsContent value="runs">
          <RunsTab runs={runs} loading={loading} />
        </TabsContent>
      </Tabs>

      <CreateCampaignDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        form={form}
        onChange={setForm}
        creating={creating}
        onSubmit={handleCreate}
      />

      <ShareReportDialog
        open={shareOpen}
        onOpenChange={(o) => { if (!o) { setShareOpen(false); setShareResult(null); } else { setShareOpen(true); } }}
        recipients={shareRecipients}
        onRecipientsChange={setShareRecipients}
        label={shareLabel}
        onLabelChange={setShareLabel}
        sharing={sharing}
        setSharing={setSharing}
        result={shareResult}
        setResult={setShareResult}
      />

      <AssignDialog
        campaign={assignOpen}
        onClose={() => setAssignOpen(null)}
        publishers={publishers}
        localeCatalog={localeCatalog}
        pubId={assignPubId}
        onPubIdChange={setAssignPubId}
        shave={assignShave}
        onShaveChange={setAssignShave}
        passthrough={assignPassthrough}
        onPassthroughChange={setAssignPassthrough}
        locales={assignLocales}
        onToggleLocale={toggleAssignLocale}
        assigning={assigning}
        onSubmit={handleAssign}
      />

      <EditAssignDialog
        assignment={editAssignOpen}
        onClose={() => setEditAssignOpen(null)}
        localeCatalog={localeCatalog}
        shave={editShave}
        onShaveChange={setEditShave}
        passthrough={editPassthrough}
        onPassthroughChange={setEditPassthrough}
        locales={editLocales}
        onToggleLocale={toggleEditLocale}
        saving={savingShave}
        onSubmit={handleSaveShave}
      />

      <ViewLocalesDialog
        assignment={viewLocalesFor}
        onClose={() => setViewLocalesFor(null)}
        localeCatalog={localeCatalog}
      />
    </div>
  );
}
